/**
 * @description 香港钱包OAuth2.0登录
 * 微信官网文档：
 * https://developers.weixin.qq.com/doc/offiaccount/OA_Web_Apps/Wechat_webpage_authorization.html
 * 登录过程：
 * html
 * -> oauth?redirect_uri=(login.cgi?url=html) （当前步骤）
 * -> login.cgi?url=html&code=xxxx（服务端写入cookie后再进行接口调用）
 * 登录成功后：
 * cookie中写入包括：qluin、qlskey、refresh_token、qlappid、qq_logtype、logintime
 */
import { sessionCache } from '../storage';
import { REDIRECT_TAG, WX_OAUTH_URL, WX_APPID, LoginSimpleApi, SESSION_SIMPLE_LOGIN_TAG, WX_OAUTH_CODE, WX_OAUTH_STATE, HK_WALLET_OPEN_URL, OAUTH_CODE_ERROR_CODES, IS_NOT_HK_USER_CODES, PASS_TICKET, HK_WALLET_OPEN_URL_MP, } from './config';
import { addRedirectTimes, isRedirectMax } from '../tools/redirect-times';
import { url as urlUtils, to, userEnv } from '@tencent/wphk-base-utils';
import { ErrorCodesType } from './typings';
import { getLoginToken } from './login-token';
/**
 * 默认错误码数组
 */
const defaultErrorCodes = {
    [ErrorCodesType.IS_NOT_HK_USER_CODES]: IS_NOT_HK_USER_CODES,
    [ErrorCodesType.OAUTH_CODE_ERROR_CODES]: OAUTH_CODE_ERROR_CODES,
};
/**
 * 简易登录
 */
class SimpleLogin {
    constructor(request, config) {
        // 页面是否处于登录中状态
        this.isLogin = false;
        if (!request) {
            throw new Error('request is not fund');
        }
        this._request = request;
        this.errorHandle = this.loginErrorHandleMiddleware(config === null || config === void 0 ? void 0 : config.errorHandleConfig);
        // 登录方法初始化
        this.init();
    }
    // 登录初始化函数
    init() {
        // 获取url上面的pass_ticket
        const passTicket = urlUtils.getUrlParam(PASS_TICKET) || '';
        // 获取url上面是否存在微信授权code
        this.oauthCode = urlUtils.getUrlParam(WX_OAUTH_CODE) || '';
        // 登录promise请求赋值
        this.loginPromise = new Promise(async (resolve, reject) => {
            if (!this.oauthCode)
                return;
            const loginSessionTag = sessionCache.getItem(SESSION_SIMPLE_LOGIN_TAG);
            if (String(loginSessionTag) === this.oauthCode) {
                // 如果url上带的oauthCode和session里已使用过的code是同一个，
                // 那么认为是本页刷新，不进行登录
                return;
            }
            const [err] = await to(this._request.post(LoginSimpleApi, {
                code: this.oauthCode,
                pass_ticket: passTicket,
            }, { headers: { 'Content-Type': 'application/json; charset=utf-8' },
            }));
            if (err === null || err === void 0 ? void 0 : err.retcode) {
                // 登录错误默认处理
                this.errorHandle(err);
                reject(err);
            }
            else {
                resolve('');
            }
        }).then(() => {
            // 登录动作完成扭转登录中状态
            this.isLogin = true;
            sessionCache.setItem(SESSION_SIMPLE_LOGIN_TAG, this.oauthCode);
        });
    }
    /**
     *
     * 登录需要满足以下条件
     * 1. 当前没有已经在登录的请求
     * 2. url上有存在oatuh code
     * 3. 当前session中有存在已登录的标识（防止手动手动刷新后重新登录）
     *  此时才会走到获取登录态接口
     */
    async waitLoginPromiseHandle() {
        const loginSessionTag = sessionCache.getItem(SESSION_SIMPLE_LOGIN_TAG);
        if (!this.isLogin && this.oauthCode && !loginSessionTag) {
            return this.loginPromise;
        }
    }
    /**
     * 跳转至香港钱包开通页/引导页
     */
    jumpToWPHKAutoOpen() {
        // 跳转香港钱包移除sessionCache
        sessionCache.removeItem(REDIRECT_TAG);
        const jumpUrl = userEnv.isInMiniProgram() ? this.getWPHKOpenGuideURLInMP() : this.getWPHKAutoOpenUrlInH5();
        location.replace(jumpUrl);
    }
    /**
     * 登录获取微信OAuth授权code
     * @returns
     */
    getWXOAuthCodeByJumpToWx(arg = {}) {
        // 获取当前页面url
        let { url = location.href } = arg;
        // 登录异常需重新登录扭转登录中状态
        this.isLogin = false;
        // 登录异常获取授权code需重新登录移除tag
        sessionCache.removeItem(SESSION_SIMPLE_LOGIN_TAG);
        // 重定向大于3次，则报错
        if (isRedirectMax(3)) {
            // 重定向次数大于3
            sessionCache.removeItem(REDIRECT_TAG);
            // 重定向3次报错暂不处理
            return false;
        }
        // 记录重定向次数
        addRedirectTimes();
        // 删除现有url链接上的code参数
        url = urlUtils.removeUrlParams(url, [WX_OAUTH_CODE, WX_OAUTH_STATE]);
        const jumpUrl = `${WX_OAUTH_URL}?appid=${WX_APPID}&redirect_uri=${encodeURIComponent(url)}&response_type=code&scope=snsapi_base&state=STATE#wechat_redirect`;
        // 拼接授权跳转链接跳转
        location.replace(jumpUrl);
        return true;
    }
    /**
     * 登录错误请求中间件
     * @param customErrorHandlers
     */
    loginErrorHandleMiddleware(customErrorHandlers = {}) {
        return (error) => {
            const customHandler = this.getLoginCustomErrorHandle(error.retcode, customErrorHandlers);
            const defaultHandler = this.getLoginDefaultErrorHandle(error.retcode);
            if (customHandler) {
                customHandler(error, (shouldExecuteDefault) => {
                    if (shouldExecuteDefault) {
                        defaultHandler();
                    }
                });
            }
            else {
                defaultHandler();
            }
        };
    }
    /**
     * 登录自定义错误处理方法
     * @param errCode
     * @param customErrorHandles
     */
    getLoginCustomErrorHandle(errCode, customErrorHandles = {}) {
        const customHandleText = Object.keys(customErrorHandles).find(errCodeText => (defaultErrorCodes[errCodeText].includes(errCode)));
        return customHandleText ? customErrorHandles[customHandleText] : undefined;
    }
    /**
     * token登陆
     * @returns
     */
    async tokenLogin() {
        const passTicket = urlUtils.getUrlParam(PASS_TICKET) || '';
        const token = getLoginToken();
        sessionStorage.setItem('try_token_login_request', '1');
        return await this._request.post(LoginSimpleApi, {
            login_token: token,
            pass_ticket: passTicket,
        }, {
            headers: { 'Content-Type': 'application/json; charset=utf-8' },
        });
    }
    /**
     * 登录默认错误处理方法
     * @param errCode
     */
    getLoginDefaultErrorHandle(errCode) {
        const defaultErrorHandle = {
            [ErrorCodesType.IS_NOT_HK_USER_CODES]: () => {
                this.jumpToWPHKAutoOpen();
            },
            [ErrorCodesType.OAUTH_CODE_ERROR_CODES]: () => {
                this.getWXOAuthCodeByJumpToWx();
            },
        };
        const defaultHandleText = Object.keys(defaultErrorHandle).find(errCodeText => (defaultErrorCodes[errCodeText].includes(errCode)));
        return defaultHandleText ? defaultErrorHandle[defaultHandleText]
            : defaultErrorHandle[ErrorCodesType.OAUTH_CODE_ERROR_CODES];
    }
    /**
     * 获取链接：香港钱包开通引导页(小程序内嵌场景)
     * 由于该引导页是mojo页面，只能接受adtag作为埋点上报字段，所以把它们（channel, spId, adtag）拼起来
     * 这里从location.href获取参数的方法参考自助开通页的处理逻辑
     */
    getWPHKOpenGuideURLInMP() {
        var _a;
        const { href } = location;
        const urlObj = new URL(href);
        const channel = (_a = (urlObj.pathname || '').split('/')) === null || _a === void 0 ? void 0 : _a[1];
        const spId = urlUtils.getUrlParam('spId', href);
        const adtag = urlUtils.getUrlAdtag(href);
        const jumpUrl = urlUtils.addUrlParams(HK_WALLET_OPEN_URL_MP, {
            adtag: [channel, spId, adtag].join('.'),
        });
        return jumpUrl;
    }
    /** 获取链接: 香港钱包自助开通页h5场景 */
    getWPHKAutoOpenUrlInH5() {
        const jumpUrl = urlUtils.addUrlParams(HK_WALLET_OPEN_URL, {
            sourceUrl: encodeURIComponent(location.href),
        });
        return jumpUrl;
    }
}
export { SimpleLogin, ErrorCodesType, };
export * as loginConfig from './config';
